import { useState } from 'react';
import { Check, KeyRound, X } from 'lucide-react';
import { Button } from 'components/ui/button';
import { Input } from 'components/ui/input';
import {
    Dialog,
    DialogContent,
    DialogHeader,
    DialogTitle,
    DialogDescription,
} from 'components/ui/dialog';
import { ReadAccess, CopyButton } from './McpSetup';
import { mcpRequest } from './McpAPI';

export default function ConnectionDialogs({
    creating,
    setCreating,
    revoking,
    setRevoking,
    onChanged,
}) {
    const [pending, setPending] = useState('');
    const [error, setError] = useState('');
    const [label, setLabel] = useState('');
    const [days, setDays] = useState('30');
    const [credential, setCredential] = useState(null);
    async function createKey(event) {
        event.preventDefault();
        setPending('create');
        setError('');
        try {
            const result = await mcpRequest('/connections', 'POST', {
                label: label.trim(),
                expiresInDays: Number(days),
            });
            // Kept only in this dialog's memory, never in storage or the query cache.
            setCredential(result);
            setCreating(false);
            setLabel('');
            await onChanged();
        } catch (err) {
            setError(err.message);
        } finally {
            setPending('');
        }
    }

    async function revoke() {
        setPending('revoke');
        setError('');
        try {
            await mcpRequest(`/connections/${revoking.id}`, 'DELETE');
            setRevoking(null);
            await onChanged();
        } catch (err) {
            setError(err.message);
        } finally {
            setPending('');
        }
    }

    return (
        <>
            {' '}
            <Dialog
                open={creating}
                onOpenChange={(open) => {
                    if (!pending) setCreating(open);
                }}
            >
                <DialogContent className="mcp-dialog">
                    <DialogHeader>
                        <DialogTitle>Create an access key</DialogTitle>
                        <DialogDescription>
                            A dedicated, read-only connection for one bot. The key is shown once.
                        </DialogDescription>
                    </DialogHeader>
                    <form onSubmit={createKey} className="mcp-form">
                        <label htmlFor="mcp-name">Connection name</label>
                        <Input
                            id="mcp-name"
                            autoFocus
                            placeholder="e.g. Operations assistant"
                            required
                            maxLength={80}
                            value={label}
                            onChange={(event) => setLabel(event.target.value)}
                        />
                        <label htmlFor="mcp-expiry">Expires after</label>
                        <select
                            id="mcp-expiry"
                            value={days}
                            onChange={(event) => setDays(event.target.value)}
                        >
                            <option value="7">7 days</option>
                            <option value="30">30 days</option>
                            <option value="90">90 days</option>
                        </select>
                        <div className="mcp-form-notice">
                            This key can read orders and customer contact details across all
                            organizations.
                        </div>
                        <ReadAccess />
                        {error && (
                            <p role="alert" className="mcp-form-error">
                                {error}
                            </p>
                        )}
                        <Button type="submit" disabled={!label.trim() || Boolean(pending)}>
                            {pending === 'create' ? 'Creating…' : 'Create key'}
                            <KeyRound />
                        </Button>
                    </form>
                </DialogContent>
            </Dialog>
            <Dialog
                open={Boolean(credential)}
                onOpenChange={(open) => {
                    if (!open) setCredential(null);
                }}
            >
                <DialogContent className="mcp-dialog">
                    <DialogHeader>
                        <DialogTitle>Save your access key</DialogTitle>
                        <DialogDescription>
                            You won’t be able to view it again. Keep it in your AI client’s secret
                            settings.
                        </DialogDescription>
                    </DialogHeader>
                    <textarea
                        className="mcp-secret"
                        aria-label="New access key"
                        readOnly
                        value={credential?.token || ''}
                        rows={3}
                    />
                    <div className="mcp-dialog-actions">
                        <CopyButton
                            value={credential?.token || ''}
                            label="Copy key"
                            onError={setError}
                        />
                        <Button onClick={() => setCredential(null)}>
                            Done
                            <Check />
                        </Button>
                    </div>
                    {error && (
                        <p role="alert" className="mcp-form-error">
                            {error}
                        </p>
                    )}
                </DialogContent>
            </Dialog>
            <Dialog
                open={Boolean(revoking)}
                onOpenChange={(open) => {
                    if (!open && !pending) setRevoking(null);
                }}
            >
                <DialogContent className="mcp-dialog">
                    <DialogHeader>
                        <DialogTitle>Revoke {revoking?.label}?</DialogTitle>
                        <DialogDescription>
                            This permanently ends this connection’s access. To reconnect, create a
                            new key or authorize the app again.
                        </DialogDescription>
                    </DialogHeader>
                    {error && (
                        <p role="alert" className="mcp-form-error">
                            {error}
                        </p>
                    )}
                    <div className="mcp-dialog-actions">
                        <Button
                            variant="outline"
                            onClick={() => setRevoking(null)}
                            disabled={Boolean(pending)}
                        >
                            Cancel
                        </Button>
                        <Button variant="destructive" onClick={revoke} disabled={Boolean(pending)}>
                            <X />
                            {pending === 'revoke' ? 'Revoking…' : 'Revoke access'}
                        </Button>
                    </div>
                </DialogContent>
            </Dialog>
        </>
    );
}
